import React, { useEffect, useMemo, useState } from "react";

const MY_TZ = "Asia/Kuala_Lumpur";

export function formatDeadline(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const date = new Intl.DateTimeFormat("ms-MY", {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: MY_TZ,
    }).format(d);
    const hour = Number(
      new Intl.DateTimeFormat("en-GB", { hour: "numeric", hour12: false, timeZone: MY_TZ }).format(d),
    );
    const minute = new Intl.DateTimeFormat("en-GB", { minute: "2-digit", timeZone: MY_TZ }).format(d);
    const suffix = hour < 12 ? "pagi" : hour < 15 ? "tengah hari" : hour < 19 ? "petang" : "malam";
    const h12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${date}, ${h12}.${String(minute).padStart(2, "0")} ${suffix}`;
  } catch (e) {
    return "";
  }
}

export function formatDateMY(iso, withTime = false) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("ms-MY", {
      day: "numeric",
      month: "short",
      year: "numeric", ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
      timeZone: MY_TZ,
    }).format(new Date(iso));
  } catch (e) {
    return "";
  }
}

function breakdown(totalSeconds) {
  const s = Math.max(0, totalSeconds);
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: Math.floor(s % 60),
  };
}

export function useCountdown(deadlineIso) {
  const target = useMemo(
    () => (deadlineIso ? new Date(deadlineIso).getTime() : null),
    [deadlineIso],
  );
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!target) return { ready: false, passed: false, parts: breakdown(0) };
  const remaining = Math.floor((target - now) / 1000);
  return { ready: true, passed: remaining <= 0, parts: breakdown(remaining) };
}

const LABELS = { days: "Hari", hours: "Jam", minutes: "Minit", seconds: "Detik" };

export function Countdown({ deadlineIso, variant = "card" }) {
  const { ready, passed, parts } = useCountdown(deadlineIso);
  if (!ready) return null;

  if (passed) {
    return (
      <p
        className="text-sm font-semibold text-[hsl(var(--muted-foreground))]"
        data-testid="countdown-closed"
      >
        Penghantaran telah ditutup.
      </p>
    );
  }

  if (variant === "inline") {
    return (
      <span data-testid="countdown-inline" className="font-semibold tabular-nums">
        {parts.days}h {String(parts.hours).padStart(2, "0")}j{" "}
        {String(parts.minutes).padStart(2, "0")}m {String(parts.seconds).padStart(2, "0")}s
      </span>
    );
  }

  if (variant === "hero") {
    return (
      <div className="grid grid-cols-4 gap-2 sm:gap-2.5" data-testid="countdown-hero">
        {["days", "hours", "minutes", "seconds"].map((key) => (
          <div
            key={key}
            className="rounded-2xl border border-border bg-card px-1.5 py-3.5 text-center sm:py-4"
          >
            <div
              key={key === "seconds" ? parts.seconds : undefined}
              className={`text-[30px] font-extrabold leading-none tabular-nums tracking-tight sm:text-[40px] ${
                key === "seconds" ? "ss-tick" : ""
              }`}
              data-testid={`countdown-hero-${key}`}
            >
              {String(parts[key]).padStart(2, "0")}
            </div>
            <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.14em] opacity-70 sm:text-[11px]">
              {LABELS[key]}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-4 gap-2" data-testid="countdown-card">
      {["days", "hours", "minutes", "seconds"].map((key) => (
        <div
          key={key}
          className="rounded-2xl border border-border bg-card px-2 py-3 text-center"
        >
          <div className="text-xl sm:text-2xl font-extrabold tabular-nums tracking-tight">
            {String(parts[key]).padStart(2, "0")}
          </div>
          <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--muted-foreground))]">
            {LABELS[key]}
          </div>
        </div>
      ))}
    </div>
  );
}
